import { PasswordInput, TextInput, Toggle, Modal } from "@carbon/react";
import { useState, useEffect, useCallback, useMemo } from "react";
import ClassSelector from "../../../Components/classSelector";
import { useLazyQuery, gql } from "@apollo/client";
import { useMutation } from "@apollo/client";
import { useNavigate, useParams } from "react-router-dom";
import { createSHA256Hash } from "../../../Utils/cryptoUtils";
import { formatDateTime } from "../../../Utils/timeutils";
const ALL_USERS_QUERY = gql`
    query AllUsers($userTypes: [String!]!, $search: String) {
      allUsers(userTypes: $userTypes, search: $search) {
          username
      }
    }
`;

const USER_BY_ID_QUERY = gql`
    query UserById($id: String!) {
      userById(id: $id) {
          id
          username
          surname
          lastname
          lastLoginAt
          __typename
          ... on Student {
            class {
              id
            }
            boarding
          }
          ... on Teacher {
            isBoarding
          }
      }
    }
`;

const EDIT_USER_MUTATION = gql`
    mutation EditUser($id: String!, $username: String, $surname: String, $lastname: String, $classId: String, $boarding: Boolean) {
      editUser(id: $id, username: $username, surname: $surname, lastname: $lastname, classId: $classId, boarding: $boarding) {
          id
          username
      }
    }
`;

const CHANGE_PASSWORD_MUTATION = gql`
    mutation ChangeUserPassword($id: String!, $newPassword: String!) {
      changeUserPassword(id: $id, newPassword: $newPassword) {
          id
          username
      }
    }
`;

export function UserEdit() {
      const navigate = useNavigate();
      const { uid } = useParams(); // Get user ID from URL
      const [usernames, setUsernames] = useState([]);
      const [originalUsername, setOriginalUsername] = useState('');
      const [loadUsers, { data: usersData }] = useLazyQuery(ALL_USERS_QUERY, {
          fetchPolicy: 'cache-first' // Use cache to avoid repeated requests
      });
      const [getUser, { data: userData, loading, error: errorLoading }] = useLazyQuery(USER_BY_ID_QUERY, {
            variables: { id: uid },
            fetchPolicy: 'cache-and-network'
      });
      const [editUser] = useMutation(EDIT_USER_MUTATION);
      const [changePassword] = useMutation(CHANGE_PASSWORD_MUTATION);

      // State variables
      const [username, setUsername] = useState('');
      const [surname, setSurname] = useState('');
      const [lastname, setLastname] = useState('');
      const [password, setPassword] = useState('');
      const [confirmPassword, setConfirmPassword] = useState('');
      const [continueEnabled, setContinueEnabled] = useState(false);
      const [passwordChangeEnabled, setPasswordChangeEnabled] = useState(false);
      const [errorMessage, setErrorMessage] = useState('');
      const [passwordErrorMessage, setPasswordErrorMessage] = useState('');
      const [userType, setUserType] = useState('student');
      const [selectedClass, setSelectedClass] = useState(null);
      const [boarding, setBoarding] = useState(false);

      const [lastLoginAt, setLastLoginAt] = useState(null);

      // Load user data - only once when component mounts
      useEffect(() => {
            if (uid) {
                getUser({ variables: { id: uid } });
                loadUsers({ variables: { userTypes: [], search: "" } });
            }
      }, [uid]); // Removed getUser and loadUsers from dependencies to prevent loops

      // Set form data once user data is loaded
      useEffect(() => {
            if (userData && userData.userById) {
                const user = userData.userById;
                setUsername(user.username);
                setSurname(user.surname);
                setLastname(user.lastname);
                setUserType(user.__typename?.toLowerCase() || 'student');
                if (user.__typename === 'Student' && user.class) {
                    setSelectedClass(user.class.id);
                }
                setLastLoginAt(user.lastLoginAt);
                if (user.__typename === 'Student') {
                    setBoarding(user.boarding ?? false);
                } else if (user.__typename === 'Teacher') {
                    setBoarding(user.isBoarding ?? false);
                }
                setOriginalUsername(user.username);
            }
      }, [userData]);

      // Process other users for username uniqueness check - memoized to prevent unnecessary recalculations
      const filteredUsernames = useMemo(() => {
            if (usersData && usersData.allUsers) {
                return usersData.allUsers
                    .map(u => u.username)
                    .filter(name => name !== originalUsername);
            }
            return [];
      }, [usersData, originalUsername]);

      useEffect(() => {
          setUsernames(filteredUsernames);
      }, [filteredUsernames]);

      // Memoized validation functions to prevent unnecessary recalculations
      const validateForm = useCallback(() => {
            if (username === '') {
                setErrorMessage('Benutzername ist erforderlich.');
                setContinueEnabled(false);
                return false;
            }
            if (username !== originalUsername && usernames.includes(username)) {
                setErrorMessage('Benutzername muss eindeutig sein.');
                setContinueEnabled(false);
                return false;
            }
            if (surname === '') {
                setErrorMessage('Vorname ist erforderlich.');
                setContinueEnabled(false);
                return false;
            }
            if (lastname === '') {
                setErrorMessage('Nachname ist erforderlich.');
                setContinueEnabled(false);
                return false;
            }
            if (userType === 'student' && !selectedClass) {
                setErrorMessage('Bitte wähle eine Klasse aus.');
                setContinueEnabled(false);
                return false;
            }
            setErrorMessage('');
            setContinueEnabled(true);
            return true;
      }, [username, originalUsername, usernames, surname, lastname, userType, selectedClass]);

      const validatePasswordForm = useCallback(() => {
            // Don't validate if both fields are empty (initial state)
            if (password === '' && confirmPassword === '') {
                setPasswordErrorMessage('');
                setPasswordChangeEnabled(false);
                return false;
            }
            if (password === '') {
                setPasswordErrorMessage('Passwort ist erforderlich.');
                setPasswordChangeEnabled(false);
                return false;
            }
            if (confirmPassword === '') {
                setPasswordErrorMessage('Passwort bestätigen ist erforderlich.');
                setPasswordChangeEnabled(false);
                return false;
            }
            if (password !== confirmPassword) {
                setPasswordErrorMessage('Passwörter stimmen nicht überein.');
                setPasswordChangeEnabled(false);
                return false;
            }
            setPasswordErrorMessage('');
            setPasswordChangeEnabled(true);
            return true;
      }, [password, confirmPassword]);

      // Use effect with the memoized validation functions
      useEffect(() => {
            validateForm();
      }, [validateForm]);

      useEffect(() => {
            validatePasswordForm();
      }, [validatePasswordForm]);

      async function handleSubmit(event) {
            event.preventDefault();
            if (!validateForm()) return;

            try {
                const classId = userType === 'student' ? selectedClass : null;
                
                const { data } = await editUser({ 
                    variables: { 
                        id: uid,
                        username, 
                        surname, 
                        lastname, 
                        classId,
                        boarding
                    } 
                });
                
                if (data && data.editUser) {
                    navigate('/admin/users');
                }
            } catch (error) {
                setErrorMessage(`Fehler beim Aktualisieren: ${error.message}`);
            }
      }

      async function handlePasswordChange(event) {
            event.preventDefault();
            if (!validatePasswordForm()) return;

            try {
                const { data } = await changePassword({ 
                    variables: { 
                        id: uid, 
                        newPassword: createSHA256Hash(password)
                    } 
                });
                
                if (data && data.changeUserPassword) {
                    setPassword('');
                    setConfirmPassword('');
                    setPasswordErrorMessage('Passwort erfolgreich geändert.');
                }
            } catch (error) {
                setPasswordErrorMessage(`Fehler beim Ändern des Passworts: ${error.message}`);
            }
      }
      // --- Delete User Mutation ---
      const DELETE_USER_MUTATION = gql`
            mutation DeleteUser($id: String!) {
              deleteUser(id: $id) {
                  id
                  username
              }
            }
      `;

      const [deleteUser, { loading: deletingUser }] = useMutation(DELETE_USER_MUTATION);
      const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);

      const handleDeleteUser = async () => {
            try {
                  await deleteUser({ variables: { id: uid } });
                  navigate('/admin/users');
            } catch (err) {
                  alert("Fehler beim Löschen des Nutzers: " + err.message);
            }
      };

      if (loading) {
            return <div>Lädt Benutzerdaten...</div>;
      }

      if (errorLoading) {
            return (
                  <div className="py-4">
                        <div className="max-w-2xl mx-auto text-center">
                              <div className="bg-red-50 border border-red-200 rounded-lg p-6">
                                    <h2 className="text-xl font-semibold text-red-800 mb-2">Nutzer nicht gefunden</h2>
                                    <p className="text-red-700 mb-4">Der angeforderte Nutzer existiert nicht oder konnte nicht geladen werden.</p>
                                    <button onClick={() => navigate('/admin/users')} className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded">
                                          Zurück zur Nutzerverwaltung
                                    </button>
                              </div>
                        </div>
                  </div>
            );
      }

      return (
            <div className="py-4">
                  <div className="max-w-2xl">
                        <div className="flex items-center justify-between">
                              <h1 className="text-2xl font-bold text-gray-900">
                                    Nutzer bearbeiten
                              </h1>
                              <button
                                    onClick={() => setIsDeleteModalOpen(true)}
                                    className="text-sm font-semibold bg-red-500 hover:bg-red-600 text-white px-5 py-2 rounded"
                              >
                                    Nutzer löschen
                              </button>
                        </div>

                        <Modal
                              open={isDeleteModalOpen}
                              danger
                              modalHeading="Nutzer wirklich löschen?"
                              primaryButtonText="Löschen"
                              secondaryButtonText="Abbrechen"
                              onRequestClose={() => setIsDeleteModalOpen(false)}
                              onRequestSubmit={handleDeleteUser}
                              primaryButtonDisabled={deletingUser}
                        >
                              <p>
                                    Wollen Sie den Nutzer <strong>{username}</strong> wirklich löschen? Dies kann nicht rückgängig gemacht werden.
                              </p>
                        </Modal>
                        
                        <form onSubmit={handleSubmit} className="mt-8 space-y-6">
                              <TextInput
                                    id="username"
                                    labelText="Benutzername"
                                    placeholder="zum Beispiel: dikoma15"
                                    className="w-full"
                                    value={username}
                                    onChange={(e) => setUsername(e.target.value)}
                                    required
                              />
                              <TextInput
                                    id="surname"
                                    labelText="Vorname"
                                    placeholder="Vorname eingeben"
                                    className="w-full"
                                    value={surname}
                                    onChange={(e) => setSurname(e.target.value)}
                                    required
                              />
                              <TextInput
                                    id="lastname"
                                    labelText="Nachname"
                                    placeholder="Nachname eingeben"
                                    className="w-full"
                                    value={lastname}
                                    onChange={(e) => setLastname(e.target.value)}
                                    required
                              />
                              {(userType === 'student') && (
                                    <ClassSelector
                                          selectedClass={selectedClass}
                                          onClassChange={setSelectedClass}
                                          enableAllClasses={false}
                                    />
                              )}
                              {userType === 'student' && (
                                    <Toggle
                                          id="toggle-boarding-student"
                                          labelText="Vollintern"
                                          labelA={null}
                                          labelB={null}
                                          toggled={boarding}
                                          onToggle={(toggled) => setBoarding(toggled)}
                                    />
                              )}
                              {userType === 'teacher' && (
                                    <Toggle
                                          id="toggle-boarding-teacher"
                                          labelText="Ist Internaterzieher"
                                          labelA={null}
                                          labelB={null}
                                          toggled={boarding}
                                          onToggle={(toggled) => setBoarding(toggled)}
                                    />
                              )}

                              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 pt-4">
                                    <p className="text-sm text-red-600 h-5">
                                          {errorMessage}
                                    </p>
                                    <button
                                          type="submit"
                                          className={`mainBtn w-full sm:w-auto text-white px-6 py-2 transition-opacity duration-200 ${
                                                continueEnabled ? 'hover:bg-blue-700' : 'opacity-50 cursor-not-allowed'
                                          }`}
                                          disabled={!continueEnabled}
                                    >
                                          Nutzer aktualisieren
                                    </button>
                              </div>
                        </form>

                        {/* Separate Password Change Section */}
                        <div className="mt-12 pt-6 border-t">
                              <h2 className="text-xl font-bold text-gray-900">
                                    Passwort ändern
                              </h2>
                              <form onSubmit={handlePasswordChange} className="mt-4 space-y-6">
                                    <PasswordInput
                                          id="password"
                                          labelText="Neues Passwort"
                                          placeholder="Neues Passwort eingeben"
                                          className="w-full"
                                          value={password}
                                          onChange={(e) => setPassword(e.target.value)}
                                          required
                                    />
                                    <PasswordInput
                                          id="confirmPassword"
                                          labelText="Neues Passwort bestätigen"
                                          placeholder="Neues Passwort erneut eingeben"
                                          className="w-full"
                                          value={confirmPassword}
                                          onChange={(e) => setConfirmPassword(e.target.value)}
                                          required
                                    />
                                    <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 pt-4">
                                          <p className="text-sm text-red-600 h-5">
                                                {passwordErrorMessage}
                                          </p>
                                          <button
                                                type="submit"
                                                className={`mainBtn w-full sm:w-auto text-white px-6 py-2 transition-opacity duration-200 ${
                                                      passwordChangeEnabled ? 'hover:bg-blue-700' : 'opacity-50 cursor-not-allowed'
                                                }`}
                                                disabled={!passwordChangeEnabled}
                                          >
                                                Passwort ändern
                                          </button>
                                    </div>
                              </form>
                        </div>
                  </div>
                  <div className="mt-12 pt-6 border-t">
                        <h2 className="text-xl font-bold text-gray-900">
                              Letzte Anmeldung
                        </h2>
                        <p className="mt-2 text-sm text-gray-600">
                              {formatDateTime(lastLoginAt) || 'Der Nutzer hat sich noch nie angemeldet.'}
                        </p>
                  </div>
            </div>
      );
}